import React from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Stack,
  Chip,
  Tooltip,
  Checkbox,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import { budgetLines, fmtMoney, payeeLabel } from '../../utils/format';
import { isRowFinalized } from '../../utils/rows';
import { EXPENSE_STATUS_META, STATUS_ROW_TINT } from '../../constants/status';
import { ellipsisSx } from '../../constants/table';

import { getBankLabel } from '../../../../../constants/thaiBanks';
// สีจริง (hex) ต่อ MUI color keyword ของ chip สถานะตรวจสอบ — ใช้ตกแต่ง tooltip ให้เข้าธีมเดียวกับตัว
// chip เอง (ไอคอน) ก็อปมาจาก components/Table/ExpensesTableApplove.js's STATUS_ACCENT_HEX
const STATUS_ACCENT_HEX = {
  default: '#94a3b8',
  warning: '#f59e0b',
  success: '#22c55e',
  error: '#ef4444',
};

const TOOLTIP_SLOT_SX = {
  bgcolor: 'rgba(33, 33, 47, 0.96)',
  borderRadius: '10px',
  boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
};

// hover ให้ chip ที่กำลังโดนชี้อยู่เด่นขึ้นมาชัดๆ (ขยับขึ้นเล็กน้อย + เงา) — pattern เดียวกับ
// chipHoverSx ใน ExpensesTableApplove.js
const CHIP_HOVER_SX = {
  cursor: 'default',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-1px) scale(1.05)',
    boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
  },
};

// เนื้อหา tooltip ของ chip สถานะ — PENDING โชว์รายชื่อผู้มีสิทธิ์ทีละคนพร้อมไอคอนคน, สถานะอื่นโชว์
// ผู้ดำเนินการ + หมายเหตุ (ถ้ามี) คั่นเส้นบางๆ — ก็อปโครงสร้างจาก renderStatusTooltipContent ใน
// ExpensesTableApplove.js (เพิ่มพารามิเตอร์ pendingLabel ให้ปรับข้อความ "รอ...จาก" ได้ต่อ chip
// เพราะที่นี่ใช้ร่วมกันทั้ง chip สถานะรายการเบิกกับ chip สถานะตรวจสอบ)
function renderStatusTooltipContent({ isPending, names, byName, note, color, pendingLabel }) {
  const accent = STATUS_ACCENT_HEX[color] || STATUS_ACCENT_HEX.default;
  return (
    <Box sx={{ py: 0.25, px: 0.25, minWidth: 150, maxWidth: 260 }}>
      {isPending ? (
        <>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 14, color: accent }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
              {pendingLabel}
            </Typography>
          </Stack>
          {names.length ? (
            <Stack spacing={0.35} sx={{ mt: 0.6, pl: 0.4 }}>
              {names.map((n) => (
                <Stack key={n} direction="row" spacing={0.6} alignItems="center">
                  <PersonIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                  <Typography sx={{ fontSize: '0.7rem' }}>{n}</Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: '0.7rem', opacity: 0.75, fontStyle: 'italic', mt: 0.5 }}
            >
              ยังไม่ได้ตั้งค่าผู้มีสิทธิ์ไว้
            </Typography>
          )}
        </>
      ) : (
        <>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <PersonIcon sx={{ fontSize: 14, color: accent }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
              ดำเนินการโดย {byName || '-'}
            </Typography>
          </Stack>
          {note && (
            <Box
              sx={{ mt: 0.6, pt: 0.6, borderTop: '1px solid rgba(255,255,255,0.18)' }}
            >
              <Typography
                sx={{ fontSize: '0.68rem', opacity: 0.85, whiteSpace: 'pre-wrap' }}
              >
                {note}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// สถานะที่ยังถือว่า "รอใครสักคนตัดสินใจ" ในขั้นตอนอนุมัติเบิก 3 stage (Expenses.status) — ชุดเดียวกับ
// getStatusGroup's 'pending' bucket ใน ExpensesTableApplove.js เป๊ะ (PREAPPROVE ไม่มีเอกสารอธิบายขั้น
// ชัดเจน จับกลุ่มไว้กับ PENDING/รออนุมัติขั้นที่ 1 เป็นค่าประมาณที่สมเหตุสมผลที่สุดเท่าที่ตรวจโค้ดได้)
// คอลัมน์ "วันที่" — โชว์วันที่สร้าง/วันที่อนุมัติ/วันที่จ่ายเงิน(คาดการณ์) 3 ค่าซ้อนกันใน cell เดียว
// มี label กำกับ + เส้นคั่นระหว่างแถว กันมั่ว (ก็อป pattern จาก components/Table/ExpensesTableApplove.js
// เป๊ะ — ไม่ได้ share component กัน แก้ที่นี่ต้องไปแก้อีกไฟล์ด้วยถ้าจะปรับ style ร่วมกัน)
const STACK_DIVIDER = (
  <Divider flexItem sx={{ width: '100%', my: 0.1, borderColor: '#94a3b8' }} />
);

function DateStackRow({ label, labelColor, children }) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center">
      <Box
        component="span"
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          color: labelColor || 'text.disabled',
          minWidth: 30,
          textAlign: 'right',
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ fontSize: '0.72rem' }}>
        {children}
      </Box>
    </Stack>
  );
}

function renderDateStack(row) {
  return (
    <Stack spacing={0.3} divider={STACK_DIVIDER}>
      <DateStackRow label="สร้าง">
        {row?.createdAt ? dayjs(row.createdAt).format('DD/MM/YYYY') : '-'}
      </DateStackRow>
      <DateStackRow label="อนุมัติ">
        {row?.date ? dayjs(row.date).format('DD/MM/YYYY') : '-'}
      </DateStackRow>
      <DateStackRow label="จ่ายเงิน" labelColor="primary.main">
        {row?.expected_payment_date
          ? dayjs(row.expected_payment_date).format('DD/MM/YYYY')
          : '-'}
      </DateStackRow>
    </Stack>
  );
}

const EXPENSE_PENDING_STATUSES = ['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'];

// เลือกชื่อผู้อนุมัติที่ "กำลังรอ" ตามสถานะปัจจุบันของแถวนั้น — โหมด ONE_STEP ใครในสามคนอนุมัติก็ได้
// เลยโชว์ทั้ง 3 ชื่อเสมอ, โหมด THREE_STEP โชว์เฉพาะคนที่ตาต่อไปจริงๆ (PENDING/PREAPPROVE รอคนที่ 1,
// APPROVE_1 รอคนที่ 2, APPROVE_2 รอคนที่ 3)
function getExpensePendingApproverNames(status, isOneStep, names) {
  if (isOneStep) return names.filter(Boolean);
  if (status === 'APPROVE_1') return [names[1]].filter(Boolean);
  if (status === 'APPROVE_2') return [names[2]].filter(Boolean);
  return [names[0]].filter(Boolean);
}

// mode (constants/modes.js) กำหนดว่า flag ไหนเป็น "หลัก" ของหน้า (payment_approval_status หน้าอนุมัติจ่าย
// เงิน / review_status หน้าตรวจสอบรายการเบิก) และ flag ไหนเป็น "รอง" (ของอีกหน้า โชว์อ่านอย่างเดียว)
export default function ExpenseRow({
  mode,
  row,
  index,
  selected,
  onToggleSelect,
  onOpenProject,
  onOpenDetail,
  onAction,
  secondaryApproverNames = [],
  expenseApproverNames = [],
  isOneStepExpenseApproval = false,
}) {
  const { secondary } = mode;
  const { codeLine, nameLine, remaining } = budgetLines(row);
  const remainingText =
    remaining != null ? `เหลือ ${fmtMoney(remaining)} บาท` : '';
  // null/undefined ของ flag หลัก (รายการเก่าก่อนมี field นี้) นับเป็น PENDING เหมือนกับที่ backend ($in
  // ที่มี null) ปฏิบัติกับมัน — กันไม่ให้ badge หายไปเฉยๆ สำหรับรายการเก่าที่ยังไม่มีใครกดตัดสินใจเลย
  const primaryStatusMeta = mode.statusMeta[row?.[mode.statusField] || 'PENDING'];
  // สถานะของตัวรายการเบิกเอง (Expenses.status) กับ flag รอง — อยู่ในคอลัมน์ "สถานะการ..." ส่วน chip
  // flag หลักอยู่ใต้รหัสในคอลัมน์ "รหัสรายการเบิก"
  const expenseStatusMeta =
    EXPENSE_STATUS_META[row?.status] ||
    (row?.status ? { label: row.status, color: 'default' } : null);
  const secondaryStatusMeta =
    secondary.statusMeta[row?.[secondary.statusField] || 'PENDING'];
  // tooltip ของ chip สถานะรายการเบิก — PENDING โชว์ผู้อนุมัติที่กำลังรออยู่ (ดึงจากตั้งค่าที่
  // /finance/expense-approvers), ตัดสินใจไปแล้วโชว์ผู้ดำเนินการล่าสุดจาก approval_logs (ไม่มี field
  // ผู้อนุมัติแบบ snapshot เดี่ยวๆ เหมือน payment_approved_by_name/reviewed_by_name เพราะขั้นตอนนี้มี
  // ได้ถึง 3 ขั้น เลยเก็บเป็น log array แทน — ดู Expenses.js model)
  const rawExpenseStatus = row?.status || 'PENDING';
  const isExpenseStatusPending = EXPENSE_PENDING_STATUSES.includes(rawExpenseStatus);
  const expensePendingNames = getExpensePendingApproverNames(
    rawExpenseStatus,
    isOneStepExpenseApproval,
    expenseApproverNames,
  );
  // เอา log entry ล่าสุด "ที่ status ตรงกับสถานะปัจจุบันจริงๆ" ไม่ใช่แค่ตัวท้ายสุดของ array เฉยๆ —
  // เพราะบาง transition (เช่น CANCEL ก่อนจะเพิ่ม CANCEL เข้า approvalStatuses Set, หรือ auto-reset
  // PREPARE→PENDING ตอนแก้เนื้อหา) ไม่ได้ถูก log ทุกครั้ง ถ้าใช้ตัวท้ายสุดเฉยๆ อาจโชว์ชื่อคนที่ทำ
  // action ก่อนหน้าผิดตัว (เช่น โชว์คนกด PREPARE ทั้งที่ตอนนี้สถานะจริงคือ CANCEL ไปแล้ว) — หาแบบนี้
  // ถ้าไม่เจอ entry ที่ status ตรงกันเลยจะได้ null (ไม่โชว์ชื่อ) ดีกว่าโชว์ชื่อผิดคน
  const latestApprovalLog =
    [...(row?.approval_logs || [])].reverse().find((log) => log.status === rawExpenseStatus) ||
    null;
  const finalized = isRowFinalized(row);
  // PENDING (รวม null/undefined ของเก่า) ยังไม่มีใครกดตัดสินใจ — 2 คอลัมน์ท้ายต้องว่างไว้
  const primaryStatus = row?.[mode.statusField] || 'PENDING';
  const hasDecision = primaryStatus !== 'PENDING';
  const actorLabel = mode.actorLabel[primaryStatus];

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        bgcolor: selected ? undefined : STATUS_ROW_TINT[primaryStatusMeta?.color],
      }}
    >
      <TableCell align="center" padding="checkbox">
        {/* ซ่อน checkbox ทั้งแถวที่จ่ายแล้ว (finalized) และแถวที่ตัดสินใจไปแล้ว (flag หลักไม่ใช่ PENDING)
        เพราะ bulk action ใช้ไม่ได้กับแถวพวกนี้อยู่แล้ว — เหลือแค่ปุ่ม "ยกเลิก" ทีละแถว */}
        {!finalized && !hasDecision && (
          <Checkbox size="small" checked={selected} onChange={onToggleSelect} />
        )}
      </TableCell>
      <TableCell align="center">
        <Box>{index + 1}</Box>
        {row?.is_urgent && (
          <Chip
            size="small"
            color="error"
            label="ด่วน"
            sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={ellipsisSx}>{row?.code || '-'}</Box>
        {/* flag หลักของหน้า — แปะใต้รหัสตรงนี้ (คอลัมน์สถานะโชว์สถานะของตัวรายการเบิก + flag รองแทน) */}
        {primaryStatusMeta && (
          <Chip
            size="small"
            variant="filled"
            color={primaryStatusMeta.color}
            label={primaryStatusMeta.label}
            sx={{
              mt: 0.5,
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </TableCell>
      <TableCell align="center">
        {/* สถานะของตัวรายการเบิกเอง (Expenses.status) + flag รอง — สอง chip ซ้อนกัน */}
        <Stack spacing={0.5} alignItems="center">
          {expenseStatusMeta && (
            <Tooltip
              arrow
              componentsProps={{ tooltip: { sx: TOOLTIP_SLOT_SX } }}
              title={renderStatusTooltipContent({
                isPending: isExpenseStatusPending,
                names: expensePendingNames,
                byName: latestApprovalLog?.actor_name,
                note: latestApprovalLog?.note,
                color: expenseStatusMeta.color,
                pendingLabel: 'รออนุมัติจาก',
              })}
            >
              <Chip
                size="small"
                variant="outlined"
                color={expenseStatusMeta.color}
                label={expenseStatusMeta.label}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                  ...CHIP_HOVER_SX,
                }}
              />
            </Tooltip>
          )}
          {secondaryStatusMeta && (
            <Tooltip
              arrow
              componentsProps={{ tooltip: { sx: TOOLTIP_SLOT_SX } }}
              title={renderStatusTooltipContent({
                isPending: (row?.[secondary.statusField] || 'PENDING') === 'PENDING',
                names: secondaryApproverNames,
                byName: row?.[secondary.byNameField],
                note: row?.[secondary.noteField],
                color: secondaryStatusMeta.color,
                pendingLabel: secondary.pendingLabel,
              })}
            >
              <Chip
                size="small"
                variant="filled"
                color={secondaryStatusMeta.color}
                label={secondaryStatusMeta.label}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                  ...CHIP_HOVER_SX,
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell align="center">{renderDateStack(row)}</TableCell>
      <TableCell
        align="center"
        onDoubleClick={() => row?.project?._id && onOpenProject()}
        sx={{ cursor: row?.project?._id ? 'pointer' : 'default' }}
      >
        <Tooltip
          title={[codeLine, nameLine, remainingText].filter(Boolean).join(' — ')}
        >
          <Box>
            <Box sx={ellipsisSx}>{codeLine}</Box>
            {nameLine && (
              <Box
                sx={{ ...ellipsisSx, fontSize: '0.72rem', color: 'text.secondary' }}
              >
                {nameLine}
              </Box>
            )}
            {remaining != null && (
              <Box
                sx={{
                  ...ellipsisSx,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: remaining < 0 ? 'error.main' : 'success.main',
                }}
              >
                {remainingText}
              </Box>
            )}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell align="center">{fmtMoney(row.net_price || row.price)}</TableCell>
      <TableCell align="center" sx={{ minWidth: 260 }}>
        {/* บัญชีผู้รับเงิน — ชื่อ / ธนาคาร / เลขบัญชี (เดิมเป็นหัวข้อกลุ่ม account accordion) */}
        <Tooltip
          title={[payeeLabel(row), getBankLabel(row?.payee?.bank), row?.payee?.account_number]
            .filter(Boolean)
            .join(' — ')}
        >
          <Box>
            <Box sx={ellipsisSx}>{payeeLabel(row)}</Box>
            {(row?.payee?.bank || row?.payee?.account_number) && (
              <Box
                sx={{ ...ellipsisSx, fontSize: '0.7rem', color: 'text.secondary' }}
              >
                {[getBankLabel(row?.payee?.bank), row?.payee?.account_number]
                  .filter(Boolean)
                  .join(' ')}
              </Box>
            )}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell onDoubleClick={onOpenDetail} sx={{ cursor: 'pointer' }}>
        <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row?.name || '-'}
        </Box>
      </TableCell>
      <TableCell align="center">
        {finalized ? (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              จ่ายแล้ว
            </Typography>
            {/* เวลาที่ status เปลี่ยนเป็น SUCCESS จริง — latestApprovalLog ด้านบนหา log entry ที่
            status ตรงกับ rawExpenseStatus อยู่แล้ว (ตอนนี้คือ 'SUCCESS' พอดี) ใช้ acted_at ของ entry
            นั้นได้เลย ไม่ต้องหาใหม่ (entry นี้ถูก push จาก payment.service.js completePayment() ตอนกด
            "จ่ายเงิน") */}
            {latestApprovalLog?.acted_at && (
              <Box sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {dayjs(latestApprovalLog.acted_at).format('DD/MM/YYYY HH:mm')}
              </Box>
            )}
          </Box>
        ) : hasDecision ? (
          // ตัดสินใจไปแล้ว — เหลือแค่ปุ่ม "ยกเลิก" ให้ล้างกลับไป PENDING แทนที่จะกดตัดสินใจซ้ำอีกรอบ
          // (ไม่มีความหมายบนแถวที่ตัดสินใจแล้ว)
          <Tooltip title={mode.resetTooltip}>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onAction('RESET');
              }}
            >
              ยกเลิก
            </Button>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title={mode.positiveAction.label}>
              <Button
                size="small"
                color="success"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction(mode.positiveAction.code);
                }}
              >
                {mode.positiveAction.label}
              </Button>
            </Tooltip>
            <Tooltip title={mode.negativeAction.label}>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction(mode.negativeAction.code);
                }}
              >
                {mode.negativeAction.label}
              </Button>
            </Tooltip>
            <Tooltip title="รอก่อน">
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction('HOLD');
                }}
              >
                รอก่อน
              </Button>
            </Tooltip>
          </Stack>
        )}
      </TableCell>
      <TableCell align="center">
        {hasDecision ? (
          <Box>
            {actorLabel && (
              <Box sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {actorLabel}
              </Box>
            )}
            <Box sx={ellipsisSx}>{row?.[mode.actorNameField] || '-'}</Box>
            {row?.[mode.actorAtField] && (
              <Box
                sx={{ ...ellipsisSx, fontSize: '0.68rem', color: 'text.secondary' }}
              >
                {dayjs(row[mode.actorAtField]).format('DD/MM/YYYY HH:mm')}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ color: 'text.disabled' }}>-</Box>
        )}
      </TableCell>
      <TableCell>
        {hasDecision && row?.[mode.noteField] ? (
          <Box
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '0.75rem' }}
          >
            {row[mode.noteField]}
          </Box>
        ) : (
          <Box sx={{ color: 'text.disabled', textAlign: 'center' }}>-</Box>
        )}
      </TableCell>
    </TableRow>
  );
}
